import * as THREE from 'three';
import type { Terrain } from '../world/Terrain';
import { clamp, damp, lerp } from '../world/noise';

const PITCH_MIN = -0.62;
const PITCH_MAX = 0.32;

export class CameraRig {
  yaw = 0;
  pitch = -0.1;
  private smoothYaw = 0;
  private smoothPitch = -0.1;
  private distance = 3.7;
  private readonly target = new THREE.Vector3();
  private readonly smoothTarget = new THREE.Vector3();
  private readonly desiredPos = new THREE.Vector3();
  private fov = 48;
  private bob = 0;
  private idleTime = 0;
  /** 0 = gameplay rig, 1 = slow cinematic orbit (title screen). */
  cinematic = 1;
  private cinematicAngle = 0;
  /** When set, the camera eases toward this yaw (used to frame rituals). */
  focusYaw: number | null = null;
  /** Impact shake, squared on use so light hits barely move the view. */
  private trauma = 0;
  private shakeTime = 0;
  private fovPunch = 0;
  /** Metres the follow target sinks for crouching and sliding. */
  lower = 0;
  /** 0..1: over the right shoulder with a little more distance, so a drawn blade stays in view. */
  shoulder = 0;
  /** QA override: [yaw offset from player facing, pitch, distance]. */
  debugView: [number, number, number] | null = null;

  constructor(private readonly camera: THREE.PerspectiveCamera, private readonly terrain: Terrain) {}

  kick(trauma: number, fov = 0): void {
    this.trauma = Math.min(1, this.trauma + trauma);
    this.fovPunch = Math.min(8, this.fovPunch + fov);
  }

  snap(player: THREE.Vector3, yaw: number): void {
    this.yaw = yaw;
    this.smoothYaw = yaw;
    this.smoothTarget.copy(player).add(new THREE.Vector3(0, 1.5, 0));
  }

  update(
    dt: number,
    player: THREE.Vector3,
    playerYaw: number,
    speed: number,
    sprint: number,
    look: THREE.Vector2,
    lookIdle: number,
    stepPhase: number,
  ): void {
    this.yaw -= look.x * 0.0024;
    this.pitch = clamp(this.pitch - look.y * 0.0021, PITCH_MIN, PITCH_MAX);

    // Drift behind the player while running if the player isn't steering the camera.
    if (lookIdle > 1.6 && speed > 1.2 && this.cinematic < 0.01) {
      let delta = playerYaw - this.yaw;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      this.yaw += delta * Math.min(1, dt * 0.55 * clamp(speed / 4, 0.3, 1.3));
      this.pitch = damp(this.pitch, -0.1, 0.6, dt);
    }

    if (this.focusYaw !== null) {
      let fd = this.focusYaw - this.yaw;
      fd = Math.atan2(Math.sin(fd), Math.cos(fd));
      this.yaw += fd * Math.min(1, dt * 1.6);
      this.pitch = damp(this.pitch, -0.14, 1.6, dt);
    }

    this.idleTime = speed < 0.2 ? this.idleTime + dt : 0;
    let yawDelta = this.yaw - this.smoothYaw;
    yawDelta = Math.atan2(Math.sin(yawDelta), Math.cos(yawDelta));
    this.smoothYaw += yawDelta * Math.min(1, dt * 16);
    this.smoothPitch = damp(this.smoothPitch, this.pitch, 16, dt);

    this.target.copy(player);
    this.target.y += 1.52 - this.lower;
    this.bob = Math.sin(stepPhase * Math.PI * 4) * 0.018 * clamp(speed / 4, 0, 1.4);
    this.target.y += this.bob;
    this.smoothTarget.x = damp(this.smoothTarget.x, this.target.x, 11, dt);
    this.smoothTarget.z = damp(this.smoothTarget.z, this.target.z, 11, dt);
    this.smoothTarget.y = damp(this.smoothTarget.y, this.target.y, 7, dt);

    const wantDistance = 3.15 + sprint * 0.55 + clamp(speed / 6, 0, 1) * 0.25 + (this.idleTime > 4 ? 0.3 : 0);
    this.distance = damp(this.distance, wantDistance, 2.2, dt);

    let yaw = this.smoothYaw;
    let pitch = this.smoothPitch;
    let distance = this.distance * (1 + clamp((1.2 - this.camera.aspect) / 0.7, 0, 1) * 0.35);
    if (this.cinematic > 0.001) {
      this.cinematicAngle += dt * 0.045;
      const cy = playerYaw - 0.42 + Math.sin(this.cinematicAngle) * 0.22;
      let d = cy - yaw;
      d = Math.atan2(Math.sin(d), Math.cos(d));
      yaw += d * this.cinematic;
      pitch = lerp(pitch, -0.12, this.cinematic);
      distance = lerp(distance, 5.2, this.cinematic);
    }

    if (this.debugView) {
      yaw = playerYaw + this.debugView[0];
      pitch = this.debugView[1];
      distance = this.debugView[2];
    }

    distance += this.shoulder * 0.45 * (1 - this.cinematic);
    const cp = Math.cos(pitch);
    const fx = Math.sin(yaw) * cp;
    const fy = Math.sin(pitch);
    const fz = Math.cos(yaw) * cp;
    // Camera right is (-cos yaw, 0, sin yaw); shift the frame so the sword arm sits clear of the body.
    const side = this.shoulder * 0.42 * (1 - this.cinematic);
    const sx = -Math.cos(yaw) * side;
    const sz = Math.sin(yaw) * side;
    this.desiredPos.set(this.smoothTarget.x - fx * distance + sx, this.smoothTarget.y - fy * distance, this.smoothTarget.z - fz * distance + sz);
    const ground = this.terrain.heightAt(this.desiredPos.x, this.desiredPos.z);
    const minY = ground + 1.3;
    if (this.desiredPos.y < minY) this.desiredPos.y = minY;
    this.camera.position.copy(this.desiredPos);
    const lookAt = this.smoothTarget.clone();
    lookAt.x += sx;
    lookAt.z += sz;
    lookAt.x += fx * 4;
    lookAt.y += fy * 4;
    lookAt.z += fz * 4;
    this.camera.lookAt(lookAt);
    this.trauma = Math.max(0, this.trauma - dt * 2.2);
    this.fovPunch *= Math.exp(-dt / 0.18);
    if (this.trauma > 0.001) {
      this.shakeTime += dt;
      const s = this.trauma * this.trauma;
      const n = (seed: number) => Math.sin(this.shakeTime * 41 + seed * 12.9898) * Math.sin(this.shakeTime * 23 + seed * 4.1);
      this.camera.position.x += n(1) * 0.05 * s;
      this.camera.position.y += n(2) * 0.05 * s;
      this.camera.rotateZ(n(3) * 0.014 * s);
    }

    // Portrait screens need a wider vertical view and a longer lens distance to keep the field in frame.
    const aspect = this.camera.aspect;
    const portrait = clamp((1.2 - aspect) / 0.7, 0, 1);
    const wantFov = 48 + sprint * 6 + portrait * 16;
    this.fov = damp(this.fov, wantFov, 3, dt);
    if (Math.abs(this.camera.fov - this.fov - this.fovPunch) > 0.01) {
      this.camera.fov = this.fov + this.fovPunch;
      this.camera.updateProjectionMatrix();
    }
    this.camera.updateMatrixWorld();
  }

  get forwardYaw(): number {
    return this.smoothYaw;
  }
}
